import { convexQuery } from "@convex-dev/react-query";
import { api } from "@convex/_generated/api";
import { isValidConvexId } from "@plasma/utils/convex-id";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { AlertCircle, ShieldAlert } from "lucide-react";

import { LoadingPanel } from "~/components/shared/loading-panel";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";
import { toConvexTableId } from "~/lib/convex-table-id";

import { LocationProfileBlocks } from "../../../../components/profile/location-profile-blocks";
import { LocationSettingsBlock } from "../../../../components/profile/location-settings-block";

const locationsGetById = api.locations.getById;

export const Route = createFileRoute("/_authenticated/locations/$id/profile")({
  component: LocationProfilePage,
});

function LocationProfilePage() {
  const { id } = Route.useParams();
  const {
    hasPermission,
    isLoading: permissionsLoading,
    hasEmployee,
  } = useEmployeePermissions();
  const canViewIntegrations = hasPermission("viewIntegrations");

  // Validate ID format before making any queries
  const validId = isValidConvexId(id);

  const { data: location } = useQuery({
    ...convexQuery(locationsGetById, {
      id: toConvexTableId("locations", id),
    }),
    enabled: validId && canViewIntegrations,
  });

  if (!validId) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Location Not Found</AlertTitle>
        <AlertDescription className="flex flex-col gap-3">
          <span>
            The location ID in the URL is invalid or the location doesn't exist.
          </span>
          <Button asChild className="w-fit" size="sm" variant="outline">
            <Link to="/locations">Back to Locations</Link>
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Loading state
  if (permissionsLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  // No employee record linked
  if (!hasEmployee) {
    return (
      <Alert>
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>No Employee Profile</AlertTitle>
        <AlertDescription>
          Your account is not linked to an employee profile. Contact your
          organization admin to set up your employee record with the appropriate
          permissions.
        </AlertDescription>
      </Alert>
    );
  }

  // No permission to view location profile
  if (!canViewIntegrations) {
    return (
      <Alert variant="destructive">
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>Access Denied</AlertTitle>
        <AlertDescription>
          You don't have permission to view location profiles. Contact your
          manager to request access.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div>
      <div className="space-y-1 py-5">
        <h2 className="body text-foreground text-weight-medium text-balance">
          Location Profile
        </h2>
        <p className="body-sm text-muted-foreground">
          Keep your location information up to date.
        </p>
      </div>
      {location ? (
        <>
          <LocationProfileBlocks location={location} locationId={id} />
          <LocationSettingsBlock location={location} />
        </>
      ) : (
        <LoadingPanel message="Loading location profile..." />
      )}
      {/* V4.1: Outlet for child routes (edit field modals) */}
      <Outlet />
    </div>
  );
}
